import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  STORAGE_KEYS,
  ROLES,
  ROLE_PERMISSIONS,
  type Permission,
  type Role,
} from '@/lib/config/constants';
import { authStorage } from '@/lib/utils/authStorage';
import { useRolesStore } from '@/store/rolesStore';
import type { User } from '@/types/domain';

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;

  paymentMethods: string[];

  features: string[];

  tenantName: string | null;

  sessionStartedAt: number | null;

  lastIdentity: string | null;

  setSession(payload: {
    user: User;
    token: string;
    refreshToken?: string;
    paymentMethods?: string[];
    features?: string[];
    tenantName?: string;
  }): void;
  setTokens(token: string, refreshToken?: string): void;
  setUser(user: User | null): void;

  syncSession(payload: {
    user?: User;
    paymentMethods?: string[];
    features?: string[];
    tenantName?: string;
  }): void;
  clearSession(): void;
  hasRole(...roles: Role[]): boolean;
  hasPermission(permission: Permission): boolean;

  hasCapability(feature: string): boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      paymentMethods: [],
      features: [],
      tenantName: null,
      sessionStartedAt: null,
      lastIdentity: null,

      setSession: ({ user, token, refreshToken, paymentMethods, features, tenantName }) => {
        const identity = `${user.id}:${user.email}`;
        const prev = get().lastIdentity;
        if (prev && prev !== identity) {
          useRolesStore.getState().setRoles([]);
        }
        set({
          user,
          token,
          refreshToken: refreshToken ?? null,
          isAuthenticated: true,
          paymentMethods: paymentMethods ?? [],
          features: features ?? [],
          tenantName: tenantName ?? null,
          sessionStartedAt: Date.now(),
          lastIdentity: identity,
        });
      },

      setTokens: (token, refreshToken) =>
        set((s) => ({ token, refreshToken: refreshToken ?? s.refreshToken })),

      setUser: (user) => set({ user }),

      syncSession: ({ user, paymentMethods, features, tenantName }) =>
        set((s) => ({
          user: user ?? s.user,
          paymentMethods: paymentMethods ?? s.paymentMethods,
          features: features ?? s.features,
          tenantName: tenantName ?? s.tenantName,
        })),

      clearSession: () =>
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          paymentMethods: [],
          features: [],
          tenantName: null,
          sessionStartedAt: null,
        }),

      hasRole: (...roles) => {
        const user = get().user;
        return !!user && roles.includes(user.role);
      },

      hasPermission: (permission) => {
        const user = get().user;
        if (!user) return false;
        if (user.role === ROLES.ADMIN) return true;
        const roleDef = useRolesStore.getState().roles.find((r) => r.id === user.role);
        const fromRole = roleDef?.permissions ?? ROLE_PERMISSIONS[user.role] ?? [];
        const fromUser = user.permissions ?? [];
        return fromRole.includes(permission) || fromUser.includes(permission);
      },

      hasCapability: (feature) => get().features.includes(feature),
    }),
    {
      name: STORAGE_KEYS.authUser,
      storage: createJSONStorage(() => authStorage),
      partialize: (s) => ({
        user: s.user,
        token: s.token,
        refreshToken: s.refreshToken,
        isAuthenticated: s.isAuthenticated,
        paymentMethods: s.paymentMethods,
        features: s.features,
        tenantName: s.tenantName,
        sessionStartedAt: s.sessionStartedAt,
        lastIdentity: s.lastIdentity,
      }),
    },
  ),
);
